/**
 * A mixin for generating a tabular measurement summary of a given service or layer.
 * @implements Summarizer
 */
import _ from "underscore";
import Field from "core/models/Field";
import s from "underscore.string";
import GeoUtils from "app/utils/GeoUtils";

//Constant which returns the appropriate label for the type of measurement.
var MEASUREMENT_LABELS = {
  length: "Length( Ft. )",
  area: "Area( Sq. Ft )",
  perimeter: "Perimeter( Ft. )",
  latitude: "Center Latitude",
  longitude: "Center Longitude",
};

/**
 * Creates a new GeographicSummarizer using the given units for tabulation.
 *
 * Note that in order to be properly summarized, a layer must contain geometries of the same type.
 * @param {esri.units} areaUnit   The area unit to use. Default is acres.
 * @param {esri.units} lengthUnit The length unit to use. Default is feet.
 */
function Summarizer(areaUnit, lengthUnit) {
  var units = window.require("esri/units");

  this.areaUnit = areaUnit || units.SQUARE_FEET;
  this.lengthUnit = lengthUnit || units.FEET;
}

Summarizer.prototype = {
  /**
   * Given a summary object, generates fields for that object and places them in the `summary_fields` field of the object.
   * @param  {Object} summary The summary object.
   * @param  {Array} [fields]  An array of fields for which to generate fields.
   * @return {Object}         The summary object.
   */
  _attachFields: function (summary, fields) {
    var fieldKeys = fields || _.keys(summary);

    fields = [];

    _.each(fieldKeys, function (key) {
      var label = MEASUREMENT_LABELS[key] || s.titleize(s.humanize(key));
      var digits = _.include(["latitude", "longitude"], key) ? 5 : 2,
        multiplier = Math.pow(10, digits);

      fields.push(
        new Field({
          id: key,
          name: key,
          label: label,
          viewOptions: {
            type: "formatted",
            format: function (value) {
              return Math.round(value * multiplier) / multiplier;
            },
          },
        })
      );
    });

    summary.summary_fields = fields;

    return summary;
  },

  /**
   * Generates a summary of the given geometry.
   * @private
   * @param  {MapObject} geometry The geometry to summarize.
   * @return {Object}          An object containing various summary attributes.
   */
  _summarizeGeometry: function (geometry) {
    return geometry.measure(this.areaUnit, this.lengthUnit);
  },

  getName: function () {
    return "Geometries";
  },

  /**
   * Generates a summary of the given geometry.
   * @param  {MapObject} geometry The geometry to summarize.
   * @return {Object}          An object containing various summary attributes as well as a summary_fields attribute containing an array of app.Field objects. These field objects can be given the returned object as a "model".
   */
  summarizeGeometry: function (geometry) {
    var center = geometry.getCenter();

    return this._attachFields(
      _.extend(this._summarizeGeometry(geometry), {
        longitude: center.getLongitude(),
        latitude: center.getLatitude(),
      })
    );
  },

  /**
   * Generates a summary of the given layer.
   * @param  {EditableLayerBase} layer The layer to summarize.
   * @return {Object}       An object containing various summary attributes as well as a summary_fields attribute containing an array of app.Field objects. These field objects can be given the returned object as a "model".
   */
  summarizeLayer: function (layer) {
    var result = {
      count: 0,
    };

    _.each(
      layer.getGeometries(),
      function (geometry) {
        var geometryType = geometry.getGeometryType();
        var summarizable =
          geometryType == "polyline" || geometryType == "polygon";

        if (summarizable) {
          var summary = this._summarizeGeometry(geometry);

          _.each(_.keys(summary), function (key) {
            result[key] = result[key] || 0;
            result[key] += summary[key];
          });
        }
        result.count++;
      },
      this
    );

    return this._attachFields(result);
  },

  /**
   * Generates a summary of each layer in the given service.
   * @param  {InternalMapService} service The service to summarize.
   * @return {Object}         An object containing a `summary_layers` attribute which contains a mapping from layer name to layer summary and a `summary_fields` attribute containing an array of app.Field objects. These field objects can be given the returned object as a "model".
   */
  summarizeService: function (service) {
    var result = {
      summary_layers: {},
    };
    var summaryResult = {};
    var self = this;

    service.eachLayer(function (layer) {
      var summary = self.summarizeLayer(layer);

      _.each(_.without(_.keys(summary), "summary_fields"), function (key) {
        summaryResult[key] = summaryResult[key] || 0;
        summaryResult[key] += summary[key];
      });

      result.summary_layers[layer.name] = summary;
    });

    result = _.extend(result, summaryResult);

    return this._attachFields(result, _.keys(summaryResult));
  },
};

export default Summarizer;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/summarize/GeographicSummarizer.js
// module id = 136
// module chunks = 1